import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter, HostBinding,
    Input, OnChanges,
    OnDestroy,
    OnInit,
    Output, SimpleChanges,
} from '@angular/core';
import { SafeStyle } from '@angular/platform-browser';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';

import { BrowserOnly } from '@core/data-providers/platform/platform.decorators';
import { Resolutions } from '@core/domain/state/core.state';
import { EnvironmentCmsUrls } from '@core/domain/types/environment-config.types';
import { getLanguageisRTL, getOrientation } from '@core/infrastructure/state/core.selectors';
import { ConfigService } from '@core/services/config/config.service';
import { CoreStorageService } from '@core/services/storage/storage.service';
import { ContentItemTree } from '@features/cms-render/domain/models/contentItemTree';
import { AssetsSharedService } from '@features/cms-render/ui/services/assets-shared/assets-shared.service';

@Component({
    selector: 'slice-flex',
    templateUrl: './slice-flex.component.html',
    styleUrls: ['./slice-flex.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SliceFlexComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
    @Input() public element: ContentItemTree;

    @Input() public direction: string;

    @Input() public resolution: Resolutions;

    @Input() public hasLogoSections: boolean = false;

    @Output() public readonly classes: EventEmitter<string> = new EventEmitter();

    public name: string;

    public direction$ = this.store.select(getLanguageisRTL);

    public isClosable: boolean;

    private readonly onDestroy$ = new Subject();

    private deletedSlices: string[] = [];

    public closeSectionData: {
        closeSectionType?: string;
        closeSectionText?: string;
        closeSectionCustomImage?: {
            image: string;
        };
    } = {};

    @HostBinding('attr.id')
    public id: string;

    public backgroundImageSetting: any;

    @HostBinding('style.background-image')
    public backgroundImage: SafeStyle | undefined;

    @HostBinding('style.flex')
    public flex: string = '1 1 100%';

    public slices: Array<{ item: ContentItemTree; slot: any; overflowH?: boolean }>;

    public hasFixedPosition: boolean = false;

    @HostBinding('class.section-position-fixed')
    public get positionFixed(): boolean {
        return this.hasFixedPosition;
    }

    @HostBinding('class.section-position-top')
    public positionTop: boolean = false;

    @HostBinding('class.section-position-bottom')
    public positionBottom: boolean = false;

    @HostBinding('class.section-position-left')
    public positionLeft: boolean = false;

    @HostBinding('class.section-position-right')
    public positionRight: boolean = false;

    constructor(
        private readonly assetsSharedService: AssetsSharedService,
        private readonly cd: ChangeDetectorRef,
        private readonly configService: ConfigService,
        private readonly elRef: ElementRef,
        private readonly storageService: CoreStorageService,
        private readonly store: Store,
    ) {
    }

    public ngOnInit(): void {
        this.setSlices(this.resolution, this.element);

        this.initComponent();
    }

    public ngOnChanges(changes: SimpleChanges) {
        if (!changes.resolution?.isFirstChange() && changes.resolution?.previousValue !== changes.resolution?.currentValue) {
            this.setSlices(this.resolution, this.element);
            this.initFixedPosition(this.resolution);
        }
    }

    private setSlices(resolution: Resolutions, piece: ContentItemTree): void {
        this.slices = [];
        const numSlots = Math.max(
            this.element.children.length,
            this.element.settings?.general?.numSlots ?? 0,
            (this.element.settings?.slots ?? []).length
        );

        if (numSlots) {
            for (let a = 0; a < numSlots; a++) {
                const objs = piece.children[a];

                if (this.hasValidResponsiveConfig(resolution, a)) {
                    this.slices.push({ item: objs, slot: piece.settings.slots[a] });
                }
            }
        } else {
            for (let child of piece.children) {
                this.slices.push({ item: child, slot: piece.settings.slots[0] });
            }
        }
    }

    private hasValidResponsiveConfig(resolution, value): boolean {
        const respConfig = this.element.settings.styles.responsiveConfig.find(
            (response) => response.id === resolution
        );

        if (respConfig?.structure) {
            return respConfig?.structure[0][value]?.toString() !== '0';
        }

        return true;
    }

    public ngAfterViewInit() {
        if (this.element.settings?.general.keepCloseSection) {
            if (this.element.settings?.general.keepCloseSection !== false && this.storageService.exists('deletedSlices', 'SESSION')) {
                const filterDeletedSlices = this.storageService.getValue<string[]>('deletedSlices', 'SESSION');
                if (filterDeletedSlices.includes(this.element.id)) {
                    this.element = null;
                    this.cd.detectChanges();
                }
            } else if (this.element.settings?.general.keepCloseSection === true) {
                this.isClosable = true;
            }
        }
    }

    public initFixedPosition(resolution: Resolutions) {
        this.hasFixedPosition = this.element?.settings?.general?.fixedPosition?.isFixedPosition;
        const fixedPositionResolution: string = resolution === Resolutions.MOBILE || resolution === Resolutions.TABLET ? 'fixedPositionDesktop' : 'fixedPositionMobile';
        this.positionTop = this.hasFixedPosition && this.element?.settings?.general?.fixedPosition?.[fixedPositionResolution] === 'top';
        this.positionBottom = this.hasFixedPosition && this.element?.settings?.general?.fixedPosition?.[fixedPositionResolution] === 'bottom';
        this.positionLeft = this.hasFixedPosition && this.element?.settings?.general?.fixedPosition?.[fixedPositionResolution] === 'left';
        this.positionRight = this.hasFixedPosition && this.element?.settings?.general?.fixedPosition?.[fixedPositionResolution] === 'right';
    }

    private initComponent(): void {
        this.id = this.element.definitionId + '-' + this.element?.id.split('-')[0];
        this.name = this.element.definitionId;
        this.isClosable = this.element.settings?.general?.closeSection;
        if (this.isClosable) {
            this.closeSectionData.closeSectionType = this.element.settings?.general?.closeSectionType ?? 'icon';
            this.closeSectionData.closeSectionText = this.element.settings?.general?.closeSectionText;
            this.closeSectionData.closeSectionCustomImage = this.element.settings?.general?.closeSectionCustomImage;
        }

        this.initFixedPosition(this.resolution);
        if (!!this.element.settings?.general?.classes && this.element.settings?.general?.classes !== '') {
            this.classes.emit(this.element.settings?.general?.classes);
        }

        this.setBackgroundImage();
    }

    @BrowserOnly<SliceFlexComponent, undefined>(undefined)
    public setBackgroundImage(): void {
        if (this.backgroundImageSetting?.landscapeImage?.image || this.backgroundImageSetting?.portraitImage?.image) {
            this.store.select(getOrientation).subscribe(orientation => {
                const assetsUrl = `${this.configService.getConfigUrl<EnvironmentCmsUrls>('cmsUrls').content}/assets/`;
                const akamaiImageVideoManagerConfig = {
                    enabled: true,
                    width: this.assetsSharedService.getWidth(this.elRef),
                };

                const landscapeImage = this.backgroundImageSetting.landscapeImage?.image
                    || this.backgroundImageSetting.landscapeImage?.path;
                const portraitImage = this.backgroundImageSetting.portraitImage?.image
                    || this.backgroundImageSetting.portraitImage?.path;

                if (landscapeImage && orientation === 'landscape') {
                    this.backgroundImage = `url(${this.assetsSharedService.getAssetUrl(landscapeImage, assetsUrl, akamaiImageVideoManagerConfig)}`;
                } else if (portraitImage && orientation === 'portrait') {
                    this.backgroundImage = `url(${this.assetsSharedService.getAssetUrl(portraitImage, assetsUrl, akamaiImageVideoManagerConfig)}`;
                }
                this.cd.detectChanges();
            });
        }
    }

    private setSection100(): void {
        const element100: HTMLElement = this.elRef?.nativeElement?.parentElement?.parentElement;
        element100.style.height = `calc(100dvh - var(--offset-body))`;

        for (const element of this.slices) {
            if (element.item.name === 'overflow-h' || element.item.definitionId === 'slice-stacked') {
                element.overflowH = true;
            }
        }
    }

    public deleteSlice(): void {
        this.isClosable = false;
        this.elRef.nativeElement.remove();

        if (this.element.settings?.general.keepCloseSection !== false) {
            if (this.storageService.exists('deletedSlices', 'SESSION')) {
                this.deletedSlices = this.storageService.getValue<string[]>('deletedSlices', 'SESSION') as string[];
            }
            this.deletedSlices.push(this.element.id);
            this.storageService.setValue('deletedSlices', this.deletedSlices, 'SESSION', 'TECHNICAL');
        }
    }

    public identify(index, item) {
        return item.item?.id;
    }

    public ngOnDestroy(): void {
        this.onDestroy$.next(null);
        this.onDestroy$.complete();
    }
}
